import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance";

// Fetch posts from API with pagination
export const fetchPosts = createAsyncThunk(
  "posts/fetchPosts",
  async ({ page = 1, perPage = 10, filter = 'all' } = {}) => {
    const response = await axiosInstance.post("/posts", { page, per_page: perPage, filter });
    return {
      posts: response.data.posts || response.data,
      pagination: response.data.pagination || { has_more: false, current_page: 1 },
      isFirstPage: page === 1,
    };
  }
);

// Fetch more posts (for infinite scroll)
export const fetchMorePosts = createAsyncThunk(
  "posts/fetchMorePosts",
  async (_, { getState }) => {
    const { currentPage, hasMore, filter } = getState().posts;
    if (!hasMore) return null;

    const nextPage = currentPage + 1;
    const response = await axiosInstance.post("/posts", { page: nextPage, per_page: 10, filter });
    return {
      posts: response.data.posts || [],
      pagination: response.data.pagination || { has_more: false, current_page: nextPage },
    };
  }
);

const postsSlice = createSlice({
  name: "posts",
  initialState: {
    posts: [],
    status: "idle", // "idle" | "loading" | "loadingMore" | "succeeded" | "failed"
    error: null,
    currentPage: 1,
    hasMore: true,
    totalPosts: 0,
    filter: 'all', // 'all' or 'following'
  },
  reducers: {
    addPost: (state, action) => {
      state.posts.unshift(action.payload);
      state.totalPosts += 1;
    },
    updatePost: (state, action) => {
      const index = state.posts.findIndex((post) => post.id === action.payload.id);
      if (index !== -1) {
        state.posts[index] = action.payload;
      }
    },
    removePost: (state, action) => {
      state.posts = state.posts.filter((post) => post.id !== action.payload);
      state.totalPosts = Math.max(0, state.totalPosts - 1);
    },
    resetPosts: (state) => {
      state.posts = [];
      state.status = "idle";
      state.currentPage = 1;
      state.hasMore = true;
      state.totalPosts = 0;
    },
    resetStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    // Update comment count for a specific post
    updatePostCommentCount: (state, action) => {
      const { postId, delta } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.comments_count = Math.max(0, (post.comments_count || 0) + delta);
      }
    },
    // Update like status for a specific post
    updatePostLikeStatus: (state, action) => {
      const { postId, isLiked, likesCount } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.is_liked = isLiked;
        post.likes_count = likesCount;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Initial fetch
      .addCase(fetchPosts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.isFirstPage) {
          state.posts = action.payload.posts;
        } else {
          state.posts = [...state.posts, ...action.payload.posts];
        }
        state.currentPage = action.payload.pagination.current_page;
        state.hasMore = action.payload.pagination.has_more;
        state.totalPosts = action.payload.pagination.total || state.posts.length;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Fetch more (infinite scroll)
      .addCase(fetchMorePosts.pending, (state) => {
        state.status = "loadingMore";
      })
      .addCase(fetchMorePosts.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          // Filter out duplicates before adding
          const existingIds = new Set(state.posts.map(p => p.id));
          const newPosts = action.payload.posts.filter(p => !existingIds.has(p.id));
          state.posts = [...state.posts, ...newPosts];
          state.currentPage = action.payload.pagination.current_page;
          state.hasMore = action.payload.pagination.has_more;
        }
      })
      .addCase(fetchMorePosts.rejected, (state, action) => {
        state.status = "succeeded"; // Don't show error, just stop loading
        state.error = action.error.message;
      });
  },
});

export const { addPost, updatePost, removePost, resetPosts, resetStatus, updatePostCommentCount, updatePostLikeStatus, setFilter } = postsSlice.actions;

// Don't persist posts - always fetch fresh from API
export default postsSlice.reducer;
